import { useState, useMemo } from 'react'
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { useIntl } from 'react-intl'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { messages } from '../messages'
import { useGetJourneyByTenantIdQuery, useGetJourneyLoadsByIdQuery } from '../../../app/redux-fetch/apiOrders'
import { JourneyLoadDto, UnassignedConsignment, UnassignedWaypoint } from '../types'
import JourneyViewLoadsTable from './JourneyViewLoadsTable'
import JourneyViewLegSection from './JourneyViewLegSection'
import ConsignmentContentsSidePanel from './ConsignmentContentsSidePanel'
import ConsignmentServicesSidePanel from './ConsignmentServicesSidePanel'

type JourneyViewSidePanelProps = {
  open: boolean
  onClose: () => void
  journeyId: number
  tenantId: number
}

const JourneyViewSidePanel = ({
  open,
  onClose,
  journeyId,
  tenantId,
}: JourneyViewSidePanelProps) => {
  const { formatMessage } = useIntl()

  const [contentsSidePanelOpen, setContentsSidePanelOpen] = useState(false)
  const [contentsConsignment, setContentsConsignment] = useState<UnassignedConsignment | null>(null)

  const [servicesSidePanelOpen, setServicesSidePanelOpen] = useState(false)
  const [servicesConsignment, setServicesConsignment] = useState<UnassignedConsignment | null>(null)
  const [servicesWaypoint, setServicesWaypoint] = useState<UnassignedWaypoint | null>(null)

  const { data: journey, isLoading: isJourneyLoading } = useGetJourneyByTenantIdQuery(
    { id: journeyId, tenantId },
    { skip: !open || !journeyId || !tenantId }
  )

  const { data: journeyLoads = [], isLoading: isLoadsLoading } = useGetJourneyLoadsByIdQuery(
    { tenantId, journeyId },
    { skip: !open || !journeyId || !tenantId }
  )

  const isLoading = isJourneyLoading || isLoadsLoading

  const loads: JourneyLoadDto[] = useMemo(() => {
    if (!journeyLoads || journeyLoads.length === 0) return []
    // Cast to unknown first, then to JourneyLoadDto[] since the API will return the new fields
    // after the backend changes are deployed
    return journeyLoads as unknown as JourneyLoadDto[]
  }, [journeyLoads])

  const consignments = useMemo(() => {
    if (!journeyLoads || journeyLoads.length === 0) return []

    const consignmentMap = new Map<number, UnassignedConsignment>()

    journeyLoads.forEach((load: any) => {
      const shipmentBookingId = load.shipmentBookingId
      if (!consignmentMap.has(shipmentBookingId)) {
        consignmentMap.set(shipmentBookingId, {
          shipmentBookingId,
          trackingNumber: load.trackingNumber,
          loadTypeName: load.loadTypeName,
          unassignedLoadCount: 0,
          temperatureControlledCount: 0,
          shipFromLocationName: load.shipFromLocationName,
          waypointCount: load.waypointCount || 0,
          deliverToLocationName: load.deliverToLocationName,
          customerName: load.customerName,
          waypoints: load.waypoints || [],
          loads: [],
        })
      }
      const consignment = consignmentMap.get(shipmentBookingId)!
      consignment.unassignedLoadCount += 1
      if (load.temperatureRange) {
        consignment.temperatureControlledCount += 1
      }
      consignment.loads = consignment.loads || []
      consignment.loads.push(load)
    })

    return Array.from(consignmentMap.values())
  }, [journeyLoads])

  const transportModeNames: Record<number, string> = {
    1: 'Air',
    2: 'Sea',
    3: 'Road',
    4: 'Rail',
  }

  const handleOpenContentsSidePanel = (consignment: UnassignedConsignment) => {
    setContentsConsignment(consignment)
    setContentsSidePanelOpen(true)
  }

  const handleCloseContentsSidePanel = () => {
    setContentsSidePanelOpen(false)
    setContentsConsignment(null)
  }

  const handleOpenServicesSidePanel = (consignment: UnassignedConsignment, waypoint: UnassignedWaypoint) => {
    setServicesConsignment(consignment)
    setServicesWaypoint(waypoint)
    setServicesSidePanelOpen(true)
  }

  const handleCloseServicesSidePanel = () => {
    setServicesSidePanelOpen(false)
    setServicesConsignment(null)
    setServicesWaypoint(null)
  }

  return (
    <SideModalWrapper
      open={open}
      onClose={onClose}
      headerLabel={`${formatMessage(messages.journey)} #${journeyId}`}
      withBottomBox={false}
      width="900px"
    >
      <Box sx={{ p: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link underline="hover" color="inherit" href="/">
            {formatMessage(messages.home)}
          </Link>
          <Link underline="hover" color="inherit" href="/shipping">
            {formatMessage(messages.shipping)}
          </Link>
          <Link underline="hover" color="inherit" href="/shipping/journeys">
            {formatMessage(messages.journeys)}
          </Link>
          <Typography color="text.primary">#{journeyId}</Typography>
        </Breadcrumbs>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {formatMessage(messages.loads)}
            </Typography>

            <JourneyViewLoadsTable loads={loads} />

            {journey?.legs && journey.legs.length > 0 && (
              <Box sx={{ mt: 4 }}>
                {journey.legs.map((leg: any, index: number) => (
                  <JourneyViewLegSection
                    key={leg.id}
                    leg={leg}
                    legNumber={index + 1}
                    transportModeName={transportModeNames[leg.transportModeId] || 'Unknown'}
                    consignments={consignments}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      <ConsignmentContentsSidePanel
        open={contentsSidePanelOpen}
        onClose={handleCloseContentsSidePanel}
        consignment={contentsConsignment}
        tenantId={tenantId}
      />

      <ConsignmentServicesSidePanel
        open={servicesSidePanelOpen}
        onClose={handleCloseServicesSidePanel}
        consignment={servicesConsignment}
        tenantId={tenantId}
        waypoint={servicesWaypoint}
      />
    </SideModalWrapper>
  )
}

export default JourneyViewSidePanel
